import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SafariLandscapeFullHeightHack, styled, useTheme } from '@streem/ui-react';
import { useRef, useState, useEffect } from 'react';
import { useDetermineUIMode, } from '../../../hooks/use_determine_ui_mode';
import { LoadingScreen } from './loading_screen';
import { UserAudioStatus } from './user_audio_status';
import { CameraInactiveScreen } from './camera_inactive_screen';
export function VideoFrameMobile({ videoController }) {
    const theme = useTheme();
    const canvasContainer = useRef(null);
    // feed what we know about the current display layout into useDetermineUIMode, and it'll tell us what display mode to use.
    const determinedUIProperties = useDetermineUIMode({
        videoController,
        canvasContainer,
    });
    const [freezeFrameImgSrc, setFreezeFrameImgSrc] = useState(undefined);
    useEffect(() => {
        const inPip = determinedUIProperties.switches.inPip;
        const enabledSubscription = videoController.enabled.subscribe(enabled => {
            if (inPip && !enabled && canvasContainer && canvasContainer.current) {
                const remoteCanvas = canvasContainer.current.querySelector('canvas');
                if (remoteCanvas) {
                    // take a snapshot of the remote video canvas
                    const canvasDataURL = remoteCanvas.toDataURL('image/jpeg', 0.4);
                    setFreezeFrameImgSrc(canvasDataURL);
                }
            }
            else {
                setFreezeFrameImgSrc(undefined);
            }
        });
        return () => {
            enabledSubscription.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoController]);
    /*
    Because this same component gets rendered for both local and remote versions of the component
    determinedUIProperties is divided into local and remote sections, so determinedVideoFrameUI
    holds only ONE of those--local for local video sessions and remote for remote videos, making
    it easy to read the settings from the same object regardless of which version it's rendering
    */
    return (_jsx(VideoContainerWrapper, { theme: theme, children: _jsxs(VideoContainer, { ref: canvasContainer, theme: theme, "data-testid": 'video-frame-test-id-' + determinedUIProperties.videoOwner, determinedVideoFrameUI: determinedUIProperties, className: `${determinedUIProperties.switches.inPip ? 'inPip' : 'notInPip'} ${determinedUIProperties.switches.inPip &&
                !determinedUIProperties.raw?.remoteVideoEnabled &&
                determinedUIProperties.videoOwner === 'remote'
                ? 'hiddenPip'
                : ''}`, children: [_jsx(FreezeFrameImg, { src: freezeFrameImgSrc, alt: "video tranistion freeze frame", style: {
                        visibility: freezeFrameImgSrc !== undefined ? 'visible' : 'hidden',
                    } }), _jsx(CameraInactiveScreen, { videoController: videoController, determinedUIProperties: determinedUIProperties, 
                    /* This will probably need to be updated when the fix for the icon-vs-text bit is resolved in CameraInactiveScreen */
                    mobileDisplay: true }), _jsx(LoadingScreen, { videoController: videoController }), _jsx(UserAudioStatus, { videoController: videoController, isPipDisplay: true, cssClassName: "userAudioStatusWrapper" })] }) }));
}
const VideoContainer = styled.div(({ determinedVideoFrameUI }) => ({
    '--base-height': '150px',
    borderRadius: determinedVideoFrameUI.switches.inPip ? '10px' : '0px',
    position: 'absolute',
    overflow: 'hidden',
    top: 0,
    left: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'inherit',
    zIndex: determinedVideoFrameUI.switches.inPip ? 3 : 2,
    ...determinedVideoFrameUI.styles,
}));
const VideoContainerWrapper = styled.div `
    border: none;
    background: ${props => props.theme.colors.black};
    position: absolute;
    width: 100%;
    height: 100%;
    ${SafariLandscapeFullHeightHack}

    .notInPip {
        transition: zIndex 0s, height 0.35s ease-in-out, width 0.35s ease-in-out,
            left 0.35s ease-in-out, top 0.35s ease-in-out;
        border-radius: 0;
    }

    // if we are in PiP mode, increase the z-index, and add some padding
    .inPip {
        transition: zIndex 0s, height 0.35s ease-in-out, width 0.35s ease-in-out,
            left 0.35s ease-in-out, top 0.35s ease-in-out, opacity 0.35s ease-in-out;
        z-index: 3;
        margin-left: 3vw;
        margin-top: 3vw;

        .userAudioStatusWrapper {
            opacity: 1;
            // take out this block and the below opacity: 0; if you're switching to an .animation() driven solution
            transition: opacity 0.25s ease-in-out 0.5s, border-radius 0.5s ease-in-out;
        }
    }

    .hiddenPip {
        opacity: 0;
    }

    .userAudioStatusWrapper {
        opacity: 0;
        transition: opacity 0s;
    }
`;
const FreezeFrameImg = styled.img `
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    object-fit: contain;
    background: inherit;
    border-radius: '20px';
`;
//# sourceMappingURL=video_frame_mobile.js.map